/**
 * The four-week review (TASKS T72, SPEC §8.5). **[DoD-10]**
 *
 * > Every 28 days, generate a review card presenting, without commentary: retention against the 85%
 * > target, with the mechanical consequence stated ... Offer to apply the change; do not apply
 * > automatically.
 *
 * This is the one surface in the app that is allowed to recommend something, and the reason it is
 * allowed is that it runs **once a month against 28 days of data**. Everything else — the three
 * numbers, the reveal, the feedback card — reports and stops. A recommendation drawn from a bad
 * night would be noise with the authority of advice.
 *
 * Two rules run through it:
 *
 *  - **The consequence is mechanical, and it is stated.** Retention above 95% does not mean "well
 *    done", it means *intake is too low*, because a scheduler that never fails you is one you are
 *    not being stretched by. Below 80% means intake is too high. The card says which lever moves.
 *  - **Nothing is applied.** The change is offered and requires an explicit tap. An app that quietly
 *    retuned itself every month would make its own behaviour unaccountable, and the learner would
 *    have no way to tell a design decision from a drift.
 *
 * Pure. No clock, no store, no DOM.
 */

import type { ManualEntry } from '../store/schema';
import { DAY_MS, DEFAULT_WINDOW_DAYS } from './metrics';
import type { GrammarShareMetric, Retention, SessionMetrics } from './metrics';

/** The review runs every 28 days (SPEC §8.5). */
export const REVIEW_PERIOD_DAYS = 28;

/** The retention target the recommendation is read against. */
export const RETENTION_TARGET = 0.85;

/** Above this, intake is too low. Below the floor, too high. */
export const RETENTION_CEILING = 0.95;
export const RETENTION_FLOOR = 0.8;

/** Conversation sessions the plan asks for in a four-week window, and how many it wants. */
export const CONVERSATION_TARGET = 3;
export const CONVERSATION_OUT_OF = 4;

/** How much the new-card cap moves when a recommendation is accepted. */
export const INTAKE_STEP = 2;
export const INTAKE_MIN = 2;
export const INTAKE_MAX = 30;

export type IntakeAdvice = 'raise' | 'lower' | 'no change' | 'not enough data';

/**
 * The mechanical consequence of a retention figure (SPEC §8.5).
 *
 * `not enough data` is a fourth outcome and a necessary one: with no due reviews in the window there
 * is no retention, and recommending a change from nothing would be the app inventing a reason to
 * retune itself.
 */
export function intakeAdvice(retention: Retention): IntakeAdvice {
  if (retention.rate === null) return 'not enough data';
  if (retention.rate > RETENTION_CEILING) return 'raise';
  if (retention.rate < RETENTION_FLOOR) return 'lower';
  return 'no change';
}

/** The cap this advice would produce. Clamped, and never applied without a tap. */
export function nextIntakeCap(current: number, advice: IntakeAdvice): number {
  const moved =
    advice === 'raise' ? current + INTAKE_STEP : advice === 'lower' ? current - INTAKE_STEP : current;
  return Math.min(INTAKE_MAX, Math.max(INTAKE_MIN, moved));
}

/** Why the lever moves, in the learner's terms. Never "well done", never "try harder". */
export function adviceReason(advice: IntakeAdvice): string {
  switch (advice) {
    case 'raise':
      // A scheduler that never fails you is one you are not being stretched by.
      return `Above ${pct(RETENTION_CEILING)} means intake is too low.`;
    case 'lower':
      return `Below ${pct(RETENTION_FLOOR)} means intake is too high.`;
    case 'no change':
      return `Between ${pct(RETENTION_FLOOR)} and ${pct(RETENTION_CEILING)} is where it should be.`;
    default:
      return 'No due reviews in this window, so there is nothing to read.';
  }
}

function pct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

// ---------------------------------------------------------------------------------------------
// Is one due?
// ---------------------------------------------------------------------------------------------

/**
 * Has 28 days passed since the last review, or since the first launch?
 *
 * Measured from the *last review* rather than from a calendar, so a learner who opens it late gets
 * the next one 28 days after he read it and not 28 days after he should have. Nothing about this
 * nags: the card becomes available, and it waits.
 */
export function reviewDue(firstLaunchAt: number, lastReviewAt: number | null, now: number): boolean {
  const since = lastReviewAt ?? firstLaunchAt;
  return now - since >= REVIEW_PERIOD_DAYS * DAY_MS;
}

// ---------------------------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------------------------

export interface Review28Input {
  retention: Retention;
  grammar: GrammarShareMetric;
  sessions: SessionMetrics;
  manual: readonly ManualEntry[];
  productiveVocabulary: number;
  currentIntakeCap: number;
  now: number;
  windowDays?: number;
}

export interface Review28Line {
  label: string;
  value: string;
  /** One line of context. A fact or a stated consequence, never encouragement. */
  note: string | null;
}

export interface Review28 {
  lines: Review28Line[];
  advice: IntakeAdvice;
  /** The cap now, and the cap the advice would produce. Equal when nothing is recommended. */
  currentIntakeCap: number;
  proposedIntakeCap: number;
  /** The one sentence the offer is made in, or null when nothing is being offered. */
  offer: string | null;
  windowDays: number;
}

/**
 * Build the review (SPEC §8.5).
 *
 * Presented "without commentary": every line is a figure and, where the brief asks for one, the
 * mechanical consequence. Nothing here interprets the person.
 */
export function review28(input: Review28Input): Review28 {
  const windowDays = input.windowDays ?? DEFAULT_WINDOW_DAYS;
  const advice = intakeAdvice(input.retention);
  const proposedIntakeCap = nextIntakeCap(input.currentIntakeCap, advice);

  const conversations = countConversations(input.manual, input.now, windowDays);

  const lines: Review28Line[] = [
    {
      label: 'Retention',
      value: input.retention.rate === null ? 'not measured' : pct(input.retention.rate),
      // The target and the consequence, together — the number alone does not say which lever moves.
      note: `target ${pct(RETENTION_TARGET)} · ${adviceReason(advice)}`,
    },
    {
      label: 'Conversations',
      value: `${conversations} of ${CONVERSATION_OUT_OF}`,
      note: `target ${CONVERSATION_TARGET}`,
    },
    {
      label: 'Grammar share',
      value: input.grammar.share === null ? 'not measured' : pct(input.grammar.share),
      note: `cap ${pct(input.grammar.cap)}${
        input.grammar.overCap && input.grammar.reviewDriven ? ' · above it because reviews came due' : ''
      }`,
    },
    {
      label: 'Productive vocabulary',
      value: `${input.productiveVocabulary}`,
      // Labelled a proxy here, as everywhere. It measures the schedule's confidence, not the ability
      // to produce a word in conversation, and those come apart where it would matter most.
      note: 'a proxy: words at a long interval, not words you can use',
    },
    {
      label: 'Session length',
      value:
        input.sessions.medianMinutes === null
          ? 'not measured'
          : `${Math.round(input.sessions.medianMinutes * 10) / 10} min`,
      note: input.sessions.count === 0 ? null : `median of ${input.sessions.count}`,
    },
    {
      label: 'Sessions a week',
      value:
        input.sessions.perWeek === null
          ? 'not measured'
          : `${Math.round(input.sessions.perWeek * 10) / 10}`,
      note: null,
    },
  ];

  return {
    lines,
    advice,
    currentIntakeCap: input.currentIntakeCap,
    proposedIntakeCap,
    offer:
      proposedIntakeCap === input.currentIntakeCap
        ? null
        : `Change new items a day from ${input.currentIntakeCap} to ${proposedIntakeCap}?`,
    windowDays,
  };
}

/**
 * Conversation sessions in the window.
 *
 * Sessions, not minutes: the plan asks for three conversations in four weeks, and two long ones are
 * not three. Entries on the same day count separately, because two conversations in a day are two
 * conversations — this is the one place a day boundary would be the wrong unit, and it is not used.
 */
function countConversations(
  manual: readonly ManualEntry[],
  now: number,
  windowDays: number,
): number {
  const from = now - windowDays * DAY_MS;
  return manual.filter(
    (entry) => entry.component === 'conversation' && entry.at > from && entry.at <= now,
  ).length;
}
